import {useCallback, useEffect, useRef} from 'react'
import {oktaAccessToken, oktaAuth} from '@/config/oktaConfig'
import {useLogout} from './useLogout'

/**
 * Session management hook inspired by B2B Portal's robust session handling
 * Handles token expiration, automatic logout, and session monitoring
 */
export const useSessionManagement = () => {
  const {logoutUserForcefully} = useLogout()
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null)
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Checks if the current token is expired or about to expire
   */
  const isTokenExpired = useCallback((): boolean => {
    try {
      const tokenStorage = localStorage.getItem('okta-token-storage')
      if (!tokenStorage) return true

      const authStateStorage = JSON.parse(tokenStorage)
      const accessToken = authStateStorage?.accessToken

      if (!accessToken?.expiresAt) return true

      // Check if token expires within the next 2 minutes (like B2B Portal)
      const expiresAt = new Date(accessToken.expiresAt * 1000)
      const now = new Date()
      const timeUntilExpiry = expiresAt.getTime() - now.getTime()
      const twoMinutes = 2 * 60 * 1000

      return timeUntilExpiry <= twoMinutes
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return true
    }
  }, [])

  /**
   * Calculates time until token expiration in seconds
   */
  const getTimeUntilExpiry = useCallback((): number => {
    try {
      const tokenStorage = localStorage.getItem('okta-token-storage')
      if (!tokenStorage) return 0

      const authStateStorage = JSON.parse(tokenStorage)
      const accessToken = authStateStorage?.accessToken

      if (!accessToken?.expiresAt) return 0

      const expiresAt = new Date(accessToken.expiresAt * 1000)
      const now = new Date()
      const timeUntilExpiry = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000))

      return timeUntilExpiry
    } catch (error) {
      console.error('Error calculating time until expiry:', error)
      return 0
    }
  }, [])

  /**
   * Sets up automatic logout timer based on token expiration
   * Similar to B2B Portal's logout timer logic
   */
  const setupLogoutTimer = useCallback(
    (secondsUntilExpiry: number): void => {
      // Clear existing timer
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
      }

      if (secondsUntilExpiry <= 0) {
        // Token already expired, logout immediately
        logoutUserForcefully()
        return
      }

      // Set timer for automatic logout
      logoutTimerRef.current = setTimeout(() => {
        console.log('Token expired, performing automatic logout')
        logoutUserForcefully()
      }, secondsUntilExpiry * 1000)

      console.log(`Logout timer set for ${secondsUntilExpiry} seconds`)
    },
    [logoutUserForcefully],
  )

  /**
   * Starts session monitoring
   * Similar to B2B Portal's session timeout handling
   */
  const startSessionMonitoring = useCallback((): void => {
    // Clear existing interval
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current)
    }

    // Check session every 2 minutes (reduced frequency to minimize logs)
    sessionCheckIntervalRef.current = setInterval(() => {
      const accessToken = oktaAccessToken()

      if (!accessToken) {
        console.log('No access token found, logging out')
        logoutUserForcefully()
        return
      }

      if (isTokenExpired()) {
        console.log('Token expired, logging out')
        logoutUserForcefully()
        return
      }

      // Update logout timer if needed
      const timeUntilExpiry = getTimeUntilExpiry()
      if (timeUntilExpiry > 0) {
        setupLogoutTimer(timeUntilExpiry)
      }
    }, 120000) // Check every 2 minutes instead of 30 seconds

    // Initial setup
    const timeUntilExpiry = getTimeUntilExpiry()
    setupLogoutTimer(timeUntilExpiry)
  }, [isTokenExpired, getTimeUntilExpiry, setupLogoutTimer, logoutUserForcefully])

  /**
   * Stops session monitoring
   */
  const stopSessionMonitoring = useCallback((): void => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = null
    }

    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current)
      sessionCheckIntervalRef.current = null
    }
  }, [])

  /**
   * Handles 401 unauthorized errors
   * Similar to B2B Portal's handle401SessionExpire
   */
  const handle401Error = useCallback(async (): Promise<void> => {
    // Prevent multiple 401 handling
    if ((globalThis as any).session401Flag) {
      console.log('401 error already being handled')
      return
    }

    ;(globalThis as any).session401Flag = true

    try {
      console.log('Handling 401 unauthorized error')

      // Check if we have a valid token
      const accessToken = oktaAccessToken()
      if (!accessToken) {
        console.log('No valid token, logging out')
        await logoutUserForcefully()
        return
      }

      // Try to renew token first
      try {
        await oktaAuth.tokenManager.renew('accessToken')
        console.log('Token renewed successfully')
        ;(globalThis as any).session401Flag = false
        return
      } catch (renewError) {
        console.log('Token renewal failed, logging out:', renewError)
        await logoutUserForcefully()
      }
    } catch (error) {
      console.error('Error handling 401:', error)
      await logoutUserForcefully()
    }
  }, [logoutUserForcefully])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSessionMonitoring()
    }
  }, [stopSessionMonitoring])

  return {
    startSessionMonitoring,
    stopSessionMonitoring,
    handle401Error,
    isTokenExpired,
    getTimeUntilExpiry,
  }
}
